/* eslint-disable react-hooks/exhaustive-deps */
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import React, { useEffect, useMemo, useState } from 'react';

import { Box, Card, CardContent, Grid, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'modules/common/spinner.component';

import HouseholdGoods from 'modules/authorizations/details/hhg/householdGoods.component';
import SimilarExpensesTable from 'modules/authorizations/details/expenses/details/similarExpensesTable.component';
import HomesaleServiceComponent from 'modules/authorizations/details/homesale/homesaleService.component';
import GlobalServiceComponent from 'modules/authorizations/details/global/globalService.component';
import RenterServices from 'modules/authorizations/details/destinationServices/destinationServicesRenter.component';
import BuyerServices from 'modules/authorizations/details/destinationServices/destinationServicesBuyer.component';
import CandidateServices from 'modules/authorizations/details/candidateServices/candidateServices.component';
import TempLiving from 'modules/authorizations/details/tempLiving/tempLiving.component';
import AuthorizationDetails from 'modules/authorizations/details/authorizationDetails.component';
import CommentsAndNotes from 'modules/authorizations/details/commentsAndNotes/commentsAndNotes.component';

import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { getAuthTaxAssistance, getTaxPoliciesByClientId } from 'modules/authorizations/store/actions/taxAssistance.actions';
import {
  createFileTask,
  getAuthorizationComments,
  getAuthorizationSummary,
  getServices,
  getTasksByFileId,
  setAuthId
} from 'modules/authorizations/store/actions/authorizations.actions';
import { getAllLineItemSummaries, getExpenseByAuthorization } from 'modules/expenses/store/expenses.actions';
import { getEmailTemplateSummaries } from 'modules/emailTemplates/store/emailTemplate.actions';
import { getVendors, getInspectors, getTitleReps, getBrokerAgents } from 'modules/authorizations/store/actions/dataManagement.actions';
import { getAuthDestinationServices } from 'modules/authorizations/store/actions/destinationService.actions';
import { getGlobalInfo } from 'modules/authorizations/store/actions/global.actions';
import { getCandidateServices } from 'modules/authorizations/store/actions/canidateTrip.actions';
import { getHhgSegments } from 'modules/authorizations/store/actions/houseHoldGoods.actions';
import { getHomesaleInfo } from 'modules/authorizations/store/actions/homesale.actions';
import { getTemporaryLivingAssignments } from 'modules/authorizations/store/actions/temporaryLiving.actions';
import {
  fetchAuthCanidateDetails,
  fetchAuthDestinationDetails,
  fetchAuthGlobalDetails,
  fetchAuthHomesaleDetails,
  fetchAuthHouseholdGoodsDetails,
  fetchAuthTempLivingDetails
} from 'modules/authorizations/utils/fetchDataDetailsService';
import { GET_EXPENSE_BY_AUTHORIZATION_FAILURE } from 'modules/expenses/store/types';
import { GET_AUTH_TAX_ASSISTANCE_FAILURE, GET_TAX_POLICIES_BY_CLIENT_FAILURE } from 'modules/authorizations/store/types/taxAssistanceTypes';
import { updateTask } from 'modules/dashboard/dashboard.actions';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  mb2: {
    marginBottom: '4px',
    marginRight: '16px'
  }
}));

const AUTH_SERVICE_TYPE_IDS = {
  temporaryLiving: 'a6431f21-754e-4546-8511-12e74170f139',
  homefindRenter: '1f0148df-2898-4d52-88f1-03f8ceafb74f',
  candidate: '3458d47e-95fd-4d57-b5f1-29e720c74d4f',
  homefindBuyer: '007e4298-676f-4e50-8791-fa6464cd5358',
  householdGoods: 'cce24947-254f-431a-97a9-2a1469161b8a',
  global: 'e5c3355e-9171-4968-a142-1a9d643a4b7d',
  homesale: '7b17cb15-a7d3-4b23-931a-ded0d4cf54a5'
};

const AUTH_MOVE_TYPE_IDS = {
  intern: '4dc01913-217d-44bd-8e4e-82c8b82f6de4',
  candidate: '0b776b0c-6610-41b5-a312-2a3b43c82679'
};

const BLOCKED_SERVICE_STATUSES = new Set(['', 'canceled', 'cancelled', 'disabled', 'none']);

const isServiceVisible = service => {
  if (!service) return false;

  if (typeof service === 'string') {
    return !BLOCKED_SERVICE_STATUSES.has(service.trim().toLowerCase());
  }

  const status = String(service?.status ?? '').trim().toLowerCase();
  const hasStatus = status.length > 0;
  const isEnabled = service?.isEnabled !== false;

  if (!isEnabled) return false;
  if (!hasStatus) return isEnabled;

  return !BLOCKED_SERVICE_STATUSES.has(status);
};

const toServicesShape = authServices => {
  if (!authServices) return {};

  if (!Array.isArray(authServices)) {
    return {
      temporaryLiving: authServices?.temporaryLiving ? { status: authServices.temporaryLiving, isEnabled: true } : null,
      homefindRenter: authServices?.homefindRenter ? { status: authServices.homefindRenter, isEnabled: true } : null,
      candidate: authServices?.candidate ? { status: authServices.candidate, isEnabled: true } : null,
      homefindBuyer: authServices?.homefindBuyer ? { status: authServices.homefindBuyer, isEnabled: true } : null,
      householdGoods: authServices?.householdGoods ? { status: authServices.householdGoods, isEnabled: true } : null,
      global: authServices?.global ? { status: authServices.global, isEnabled: true } : null,
      homesale: authServices?.homesale ? { status: authServices.homesale, isEnabled: true } : null,
      taxAssistance: authServices?.taxAssistance ? { status: authServices.taxAssistance, isEnabled: true } : null
    };
  }

  const serviceByType = {};
  authServices.forEach(s => {
    const typeId = String(s?.authorizationFileServiceTypeId || '').toLowerCase();
    if (typeId) {
      serviceByType[typeId] = {
        status: s?.serviceStatus,
        isEnabled: s?.isEnabled !== false
      };
    }
  });

  return {
    temporaryLiving: serviceByType[AUTH_SERVICE_TYPE_IDS.temporaryLiving.toLowerCase()],
    homefindRenter: serviceByType[AUTH_SERVICE_TYPE_IDS.homefindRenter.toLowerCase()],
    candidate: serviceByType[AUTH_SERVICE_TYPE_IDS.candidate.toLowerCase()],
    homefindBuyer: serviceByType[AUTH_SERVICE_TYPE_IDS.homefindBuyer.toLowerCase()],
    householdGoods: serviceByType[AUTH_SERVICE_TYPE_IDS.householdGoods.toLowerCase()],
    global: serviceByType[AUTH_SERVICE_TYPE_IDS.global.toLowerCase()],
    homesale: serviceByType[AUTH_SERVICE_TYPE_IDS.homesale.toLowerCase()],
    taxAssistance: null
  };
};

const createFilePreviewOptions = (authServices, moveType) => {
  const services = toServicesShape(authServices);
  const normalizedMoveType = String(moveType || '').toLowerCase();
  const isCandidateOrInternMoveType =
    normalizedMoveType === 'candidate' ||
    normalizedMoveType === 'intern' ||
    normalizedMoveType === AUTH_MOVE_TYPE_IDS.candidate ||
    normalizedMoveType === AUTH_MOVE_TYPE_IDS.intern;
  const previewOptions = ['All Expenses'];
  previewOptions.push('Main Authorization Details');
  previewOptions.push('Comments');

  if (isServiceVisible(services?.taxAssistance)) previewOptions.push('Tax Assistance');
  if (isServiceVisible(services?.homesale)) previewOptions.push('Homesale');
  if (isServiceVisible(services?.global)) previewOptions.push('Global');
  if (isServiceVisible(services?.temporaryLiving)) previewOptions.push('Temporary Living');
  if (isServiceVisible(services?.householdGoods)) previewOptions.push('HHG');
  if (isServiceVisible(services?.homefindRenter)) previewOptions.push('Destination Renter');
  if (isServiceVisible(services?.homefindBuyer)) previewOptions.push('Destination Buyer');

  if (
    isServiceVisible(services?.homefindBuyer) ||
    isServiceVisible(services?.candidate) ||
    isCandidateOrInternMoveType
  ) {
    previewOptions.push('Candidate Services');
  }

  return previewOptions;
};

const normalizeAuthOption = option => {
  if (option == null) return null;

  if (typeof option !== 'object') {
    return {
      value: option,
      label: String(option)
    };
  }

  const value = option.value ?? option.id ?? option.fileId ?? option.authorizationId ?? option.authId ?? null;
  const fileId = option.fileId ?? option.authorizationId ?? option.authId ?? value;
  const transfereeName = option.transfereeName ?? option.fullName ?? option.authorizationName ?? '';
  const isMissingName = value => {
    if (!value) return true;
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'n/a' || normalized === 'na' || normalized === '-';
  };
  const hasUsableLabel = option.label && !isMissingName(String(option.label).split('(')[0]);

  return {
    value,
    label:
      (hasUsableLabel ? option.label : null) ??
      option.displayLabel ??
      (transfereeName ? `${transfereeName} (${fileId})` : String(fileId ?? ''))
  };
};

const getTransfereeName = details => {
  if (!details) return '';

  const summary = details?.authDetails?.summary ?? {};
  const auth = details?.authorizationDetails ?? {};
  const eeMember = (details?.familyMembers ?? []).find(member => member?.familyRelationshipTypeId === '83ff7cbb-6ec5-4162-a449-9205b0e5c921');
  const eeMemberName = [eeMember?.firstName, eeMember?.lastName].filter(Boolean).join(' ').trim();

  const candidates = [
    summary.transfereeName,
    auth.transfereeName,
    auth.eeFullName,
    auth.fullName,
    eeMemberName
  ];

  const value = candidates.find(candidate => {
    if (!candidate) return false;
    const normalized = String(candidate).trim().toLowerCase();
    return normalized !== 'n/a' && normalized !== 'na' && normalized !== '-';
  });

  return value || '';
};

const getSummaryAuthLabel = summary => {
  if (!summary) return '';

  const fileId = summary.fileId ?? summary.authorizationId ?? summary.authId ?? '';
  const rawTransfereeName = summary.transfereeName ?? summary.fullName ?? summary.authorizationName ?? '';
  const normalizedName = String(rawTransfereeName || '').trim();
  const transfereeName = ['n/a', 'na', '-'].includes(normalizedName.toLowerCase()) ? '' : normalizedName;

  if (transfereeName && fileId) return `${transfereeName} (${fileId})`;
  return transfereeName || String(fileId || '');
};

const DynamicFilePreviewCard = props => {
  const { location, vendors, inspectors, titleReps, brokerAgents, authOptions } = props;

  const [authId, setAuthIdLocal] = useState(props.authId ?? null);
  const [contentToPreview, setContentToPreview] = useState('All Expenses');
  const [isLoading, setIsLoading] = useState(false);
  const [clientTaxPolicies, setClientTaxPolicies] = useState(null);
  const [allLineItemSummaries, setAllLineItemSummaries] = useState([]);
  const details = props.authorizationDetails && authId ? props.authorizationDetails[authId] : null;
  const authServices =
    (Array.isArray(details?.services) && details.services.length > 0 && details.services) ||
    (Array.isArray(details?.authDetails?.authServices) && details.authDetails.authServices.length > 0 && details.authDetails.authServices) ||
    details?.authDetails?.authServices ||
    details?.services ||
    null;
  const moveType = details?.authorizationDetails?.moveTypeId ?? '';
  const previewOptions = createFilePreviewOptions(authServices, moveType);
  const allEnums = props.enumerations?.allEnums ?? {};
  const emailTemplates = props.templates?.emailTemplates ?? [];
  const normalizedMoveTypeId = String(
    details?.authorizationDetails?.moveTypeId ?? details?.authDetails?.summary?.moveTypeId ?? ''
  ).toLowerCase();
  const isInternMoveType = normalizedMoveTypeId === AUTH_MOVE_TYPE_IDS.intern;
  const isCandidateMoveType = normalizedMoveTypeId === AUTH_MOVE_TYPE_IDS.candidate;
  const normalizedAuthOptions = useMemo(() => (authOptions ?? []).map(normalizeAuthOption).filter(option => option?.value), [authOptions]);
  const selectedAuthOption = useMemo(
    () => normalizedAuthOptions.find(option => String(option.value).toLowerCase() === String(authId ?? '').toLowerCase()) ?? null,
    [normalizedAuthOptions, authId]
  );

  const detailsForPreview = useMemo(
    () => {
      if (!details) return null;

      const existingSummary = details?.authDetails?.summary ?? {};
      const auth = details?.authorizationDetails ?? {};
      const resolvedTransfereeName = getTransfereeName(details);
      return {
        ...details,
        authExpenses: details?.expenses ?? null,
        authDetails: {
          ...(details?.authDetails ?? {}),
          summary: {
            ...existingSummary,
            authorizationId: existingSummary.authorizationId ?? auth.authorizationId,
            transfereeName: resolvedTransfereeName,
            clientName: existingSummary.clientName ?? auth.clientName,
            moveType: existingSummary.moveType ?? auth.moveType,
            moveTypeId: existingSummary.moveTypeId ?? auth.moveTypeId
          }
        }
      };
    },
    [details]
  );

  const clientInfo =
    (details?.authorizationDetails?.clientId && props.clientsInfo?.[details.authorizationDetails.clientId]) ||
    (details?.authDetails?.summary?.clientName && props.unmappedClients
      ? props.unmappedClients.find(e => e.description === details?.authDetails?.summary.clientName)
      : null);

  const classes = useStyles();

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const loadBaseAuthData = async selectedAuthId => {
    if (!selectedAuthId) return;

    setIsLoading(true);
    await props.setAuthId(selectedAuthId);

    const summaryResp = await props.getAuthorizationSummary(selectedAuthId);
    if (summaryResp?.type) {
      await props.getServices(selectedAuthId);
    }
    if (!allLineItemSummaries || allLineItemSummaries.length === 0) {
      const allLineItemSummariesResp = await props.getAllLineItemSummaries(selectedAuthId);
      setAllLineItemSummaries(allLineItemSummariesResp?.response ?? []);
    }
    if (!vendors || vendors.length === 0) await props.getVendors();
    if (!inspectors || inspectors.length === 0) await props.getInspectors();
    if (!titleReps || titleReps.length === 0) await props.getTitleReps();
    if (!brokerAgents || brokerAgents.length === 0) await props.getBrokerAgents();
    if (!emailTemplates || emailTemplates.length === 0) await props.getEmailTemplateSummaries();

    setIsLoading(false);
  };

  const getTaxAssistanceData = async selectedAuthId => {
    if (!selectedAuthId || !clientInfo?.id) return;

    setIsLoading(true);

    if (!details?.authTaxAssistance?.taxAssistance) {
      const taxAssistanceResp = await props.getAuthTaxAssistance(selectedAuthId);
      if (taxAssistanceResp.type === GET_AUTH_TAX_ASSISTANCE_FAILURE) {
        props.showToast('Failed to retrieve tax policies, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    }

    if (!clientTaxPolicies) {
      const { type, response } = await props.getTaxPoliciesByClientId(clientInfo.id);
      if (type === GET_TAX_POLICIES_BY_CLIENT_FAILURE) {
        props.showToast('Failed to retrieve client tax policies, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      } else {
        setClientTaxPolicies(response);
      }
    }

    setIsLoading(false);
  };

  const loadSelectedServiceData = async () => {
    if (!authId) return;

    await props.setAuthId(authId);

    if (contentToPreview === 'All Expenses') {
      if (!details?.expenses) {
        setIsLoading(true);
        const resp = await props.getExpenseByAuthorization(authId);
        if (resp.type === GET_EXPENSE_BY_AUTHORIZATION_FAILURE) {
          props.showToast('Failed to retrieve submitted expenses, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
        setIsLoading(false);
      }
      return;
    }

    if (contentToPreview === 'Tax Assistance') {
      await getTaxAssistanceData(authId);
      return;
    }

    if (contentToPreview === 'Main Authorization Details' || contentToPreview === 'Comments') {
      setIsLoading(true);

      if (!details?.fileTasks) {
        await props.getTasksByFileId(authId);
      }

      if (!details?.commentsAndNotes || !details?.commentsAndNotesWithAttachments) {
        await props.getAuthorizationComments(authId);
      }

      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'HHG') {
      setIsLoading(true);
      await fetchAuthHouseholdGoodsDetails(details, authId, props);
      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'Homesale') {
      setIsLoading(true);
      await fetchAuthHomesaleDetails(details, authId, props);
      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'Global') {
      setIsLoading(true);
      await fetchAuthGlobalDetails(details, authId, props);
      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'Temporary Living') {
      setIsLoading(true);
      await fetchAuthTempLivingDetails(details, authId, props);
      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'Destination Renter' || contentToPreview === 'Destination Buyer') {
      setIsLoading(true);
      await fetchAuthDestinationDetails(details, authId, props);
      setIsLoading(false);
      return;
    }

    if (contentToPreview === 'Candidate Services') {
      setIsLoading(true);
      await fetchAuthCanidateDetails(details, authId, props);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setAuthIdLocal(props.authId ?? null);
  }, [props.authId]);

  useEffect(() => {
    if (props.authId || authId || normalizedAuthOptions.length === 0) return;
    setAuthIdLocal(normalizedAuthOptions[0].value);
  }, [props.authId, authId, normalizedAuthOptions]);

  useEffect(() => {
    if (!authId) return;
    setContentToPreview('All Expenses');
    setClientTaxPolicies(null);
    loadBaseAuthData(authId);
  }, [authId]);

  useEffect(() => {
    loadSelectedServiceData();
  }, [contentToPreview, authId]);

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ marginTop: '10px' }}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between">
              <Box sx={{ display: 'flex' }}>
                <Tooltip title="Estimates" key="Estimates" placement="right">
                  <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  File Preview
                </Typography>
              </Box>

              {normalizedAuthOptions.length > 0 && (
                <Box sx={{ display: 'flex' }}>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem', paddingTop: '16px' }}>
                      Authorization
                    </Typography>
                  </div>
                  <Select
                    labelId={'authSelected'}
                    key={'authSelected'}
                    label={''}
                    name={'authSelected'}
                    value={authId ?? ''}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    onChange={e => {
                      setAllLineItemSummaries([]);
                      setAuthIdLocal(e.target.value);
                    }}
                  >
                    {normalizedAuthOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{ margin: 'dense' }}>
                        {option.label}
                      </MenuItem>
                    ))}

                  </Select>
                </Box>
              )}

              <Box sx={{ display: 'flex' }}>
                <div className={classes.mb2}>
                  <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem', paddingTop: '16px' }}>
                    {(selectedAuthOption?.label || getSummaryAuthLabel(detailsForPreview?.authDetails?.summary)) &&
                      `${selectedAuthOption?.label || getSummaryAuthLabel(detailsForPreview?.authDetails?.summary)} - `}
                    Content To Preview
                  </Typography>
                </div>
                <Select
                  labelId={'contentToPreview'}
                  key={'contentToPreview'}
                  label={''}
                  name={'contentToPreview'}
                  value={contentToPreview}
                  sx={{ maxWidth: '360px', minWidth: '180px' }}
                  onChange={e => {
                    setContentToPreview(e.target.value);
                  }}
                >
                  {previewOptions.map(opt => (
                    <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ width: '30px' }}>

              </Box>
            </div>
          </CardContent>

          <CardContent sx={{ padding: '10px', marginLeft: '10px', paddingTop: '0px' }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {authId && (
                  <Box>
                    {contentToPreview === 'HHG' && !isLoading && (
                      <HouseholdGoods
                        details={detailsForPreview}
                        clientInfo={clientInfo}
                        id={authId}
                        history={props.history}
                        location={location}
                        isReadOnly={true}
                        vendors={vendors}
                        enumerations={allEnums}
                        emailTemplates={emailTemplates}
                      />
                    )}

                    {contentToPreview === 'All Expenses' && !isLoading && detailsForPreview && (
                      <SimilarExpensesTable
                        details={detailsForPreview}
                        authId={authId}
                        history={props.history}
                        location={location}
                        summaryExpenseLines={allLineItemSummaries}
                      />
                    )}

                    {contentToPreview === 'Main Authorization Details' && !isLoading && detailsForPreview && (
                      <AuthorizationDetails
                        details={detailsForPreview}
                        clientInfo={clientInfo}
                        id={authId}
                        isReadOnly={true}
                        vendors={vendors}
                        enumerations={props.enumerations}
                        emailTemplates={emailTemplates}
                        fileTasks={detailsForPreview?.fileTasks ?? []}
                        createFileTask={props.createFileTask}
                        updateTask={props.updateTask}
                        showToast={props.showToast}
                        history={props.history}
                        resetSurveyApiCall={async () => true}
                      />
                    )}

                    {contentToPreview === 'Comments' && !isLoading && detailsForPreview && (
                      <CommentsAndNotes
                        authorizationId={authId}
                        details={detailsForPreview}
                        viewMode="fullView"
                        enumerations={props.enumerations}
                        clientInfo={clientInfo}
                        isReadOnly={true}
                        allowReadOnlySelection={true}
                      />
                    )}

                    {contentToPreview === 'Homesale' && !isLoading && detailsForPreview?.homesale && (
                      <HomesaleServiceComponent
                        isReadOnly={true}
                        details={detailsForPreview}
                        clientInfo={clientInfo}
                        id={authId}
                        history={props.history}
                        brokerAgents={brokerAgents}
                        vendors={vendors}
                        inspectors={inspectors}
                        titleReps={titleReps}
                        location={location}
                        enumerations={props.enumerations}
                        emailTemplates={emailTemplates}
                      />
                    )}

                    {contentToPreview === 'Global' && !isLoading && detailsForPreview?.global && (
                      <GlobalServiceComponent
                        isReadOnly={true}
                        details={detailsForPreview}
                        clientInfo={clientInfo}
                        id={authId}
                        history={props.history}
                        location={location}
                        enumerations={allEnums}
                        vendors={vendors}
                        emailTemplates={emailTemplates}
                      />
                    )}

                    {contentToPreview === 'Temporary Living' && !isLoading && (
                      <TempLiving
                        details={detailsForPreview}
                        clientInfo={clientInfo}
                        authId={authId}
                        history={props.history}
                        location={location}
                        temporaryLivingAssignments={
                          detailsForPreview?.authTemporaryLiving || detailsForPreview?.temporaryLivingAssignments || []
                        }
                        enumerations={allEnums}
                        emailTemplates={emailTemplates}
                        allVendorOptions={vendors || []}
                        isReadOnly={true}
                      />
                    )}

                    {contentToPreview === 'Destination Renter' && !isLoading && detailsForPreview?.destinationServices && (
                      <RenterServices
                        details={detailsForPreview}
                        brokerAgents={brokerAgents}
                        authId={authId}
                        history={props.history}
                        location={location}
                        isReadOnly={true}
                        addresses={detailsForPreview?.addresses || []}
                        emailTemplates={emailTemplates}
                        vendors={vendors}
                        enumerations={allEnums}
                        clientInfo={clientInfo}
                      />
                    )}

                    {contentToPreview === 'Destination Buyer' && !isLoading && detailsForPreview?.destinationServices && (
                      <BuyerServices
                        details={detailsForPreview}
                        brokerAgents={brokerAgents}
                        id={authId}
                        history={props.history}
                        location={location}
                        isReadOnly={true}
                        emailTemplates={emailTemplates}
                        vendors={vendors}
                        enumerations={allEnums}
                        clientInfo={clientInfo}
                      />
                    )}

                    {contentToPreview === 'Candidate Services' && !isLoading && detailsForPreview && (
                      <Grid>
                        {(isCandidateMoveType || isInternMoveType) && (
                          <CandidateServices
                            details={detailsForPreview}
                            authId={authId}
                            brokerAgents={brokerAgents}
                            history={props.history}
                            location={location}
                            isReadOnly={true}
                            enumerations={props.enumerations}
                            emailTemplates={emailTemplates}
                            vendors={vendors}
                          />
                        )}
                      </Grid>
                    )}

                    {(isLoading || !detailsForPreview) && <Spinner />}
                  </Box>
                )}
                {!authId && <Box>Select an authorization above to see preview content.</Box>}
              </Grid>
            </Grid>
          </CardContent>
        </Box>
      </Card>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { authorizationDetails, isLoading, currentAuthId, brokerAgents, vendors, inspectors, titleReps } = state.authorizations;
  const { unmappedClients, clientsInfo } = state.clients;

  return {
    authorizationDetails,
    isLoading,
    currentAuthId,
    unmappedClients,
    clientsInfo,
    brokerAgents,
    vendors,
    inspectors,
    titleReps,
    enumerations: state.enumerations,
    templates: state.templates
  };
};

export default compose(
  connect(mapStateToProps, {
    showToast,
    getAuthTaxAssistance,
    getTaxPoliciesByClientId,
    getAuthorizationSummary,
    getServices,
    setAuthId,
    getAuthorizationComments,
    getTasksByFileId,
    createFileTask,
    updateTask,
    getEmailTemplateSummaries,
    getExpenseByAuthorization,
    getVendors,
    getInspectors,
    getTitleReps,
    getBrokerAgents,
    getAuthDestinationServices,
    getGlobalInfo,
    getCandidateServices,
    getHhgSegments,
    getHomesaleInfo,
    getTemporaryLivingAssignments,
    getAllLineItemSummaries
  })
)(DynamicFilePreviewCard);
